import { Component, Input, OnInit } from '@angular/core';
import { ReportBuilderService } from './report-builder.service';
import { SchemaObject, TableColumn } from './openapi-schema';

@Component({
    selector: 'kx-ai-report-viewer',
    templateUrl: './kx-ai-report-viewer.component.html',
    styleUrls: ['./kx-ai-report-viewer.component.scss'],
    standalone: false
})
export class KxAiReportViewerComponent implements OnInit {
  @Input() schema: SchemaObject = {};
  @Input() data: any={};
  public reportTitle: string = 'Report';
  insightsCollapsed = false;
  
  sections: any[] = [];
  columns: TableColumn[] = [];
  leafColumns: TableColumn[] = [];
  tableData: any[] = [];
  metadata: any = {};
  columnTotals: { [key: string]: number } = {};
  hasNumericColumns: boolean = false;

  // Pagination state
  pageSize: number = 10;
  pageNo: number = 1;
  pageSizes: number[] = [5, 10, 20, 50, 100];
  pagedData: any[] = [];

  // Pin header/footer state
  pinHeaderFooter: boolean = false;
  // Collapse section header state
  sectionHeaderCollapsed: boolean = false;

  inputPageNo: number = 1;

  constructor(private reportService: ReportBuilderService) {}

  ngOnInit() {
    this.initializeReport();
    this.updatePagedData();
    this.inputPageNo = this.pageNo;
  }

  private initializeReport(): void {
    if (!this.schema || !this.data) return;

    // Handle metadata section
    if (this.schema.properties?.metadata) {
      this.metadata = this.data.metadata || {};
      this.sections = this.reportService.getMetadataSections(this.schema.properties.metadata);
    }

    // Handle data section
    if (this.schema.properties?.data) {
      const dataSchema = this.schema.properties.data;
      if (dataSchema.type === 'array' && dataSchema.items) {
        // Build columns from the items schema
        this.columns = this.reportService.buildColumns(dataSchema.items);
        this.leafColumns = this.reportService.getLeaves(this.columns);
        this.tableData = this.data.data || [];
        this.reportTitle  = this.data.title || this.reportTitle
        
        // Calculate totals for numeric columns
        this.calculateColumnTotals();
        // Always update pagedData after data changes
        this.updatePagedData();
      }
    }
  }

  private calculateColumnTotals(): void {
    this.columnTotals = {};
    this.hasNumericColumns = false;
    
    this.leafColumns.forEach(col => {
      if (col.type === 'number') {
        this.hasNumericColumns = true;
        const total = this.tableData.reduce((sum, row) => {
          const value = this.getValue(row, col.key);
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
        this.columnTotals[col.key] = total;
      }
    });
  }

  getValue(data: any, path: string): any {
    if (!data || !path) return undefined;
    return this.reportService.getValue(data, path);
  }

  formatValue(value: any, type: string): string {
    if (value === null || value === undefined) return '-';
    
    switch (type) {
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : value;
      case 'date':
        return new Date(value).toLocaleDateString();
      default:
        return String(value);
    }
  }

  isNumericColumn(col: TableColumn): boolean {
    return col.type === 'number';
  }

  getColumnTotal(col: TableColumn): string {
    return this.formatValue(this.columnTotals[col.key], 'number');
  }

  updatePagedData(): void {
    if (this.pageSize === -1) {
      this.pagedData = this.tableData.slice();
      this.pageNo = 1;
    } else {
      const start = (this.pageNo - 1) * this.pageSize;
      const end = start + this.pageSize;
      this.pagedData = this.tableData.slice(start, end);
    }
  }

  // Handle page size change, including 'All'
  onPageSizeChange(size: number): void {
    if (size === -1) {
      this.pageSize = -1;
      this.pageNo = 1;
      this.pagedData = this.tableData.slice();
    } else {
      this.pageSize = size;
      this.pageNo = 1;
      this.updatePagedData();
    }
  }

  goToPage(page: number): void {
    if (this.pageSize === -1) return; // Disable navigation in 'All' mode
    if (page < 1) page = 1;
    if (page > this.totalPages) page = this.totalPages;
    this.pageNo = page;
    this.inputPageNo = this.pageNo;
    this.updatePagedData();
  }

  onPageInputBlur(): void {
    this.goToPage(this.inputPageNo);
  }

  onPageInputEnter(): void {
    this.goToPage(this.inputPageNo);
  }

  goToFirst(): void { if (this.pageSize !== -1) { this.goToPage(1); } }
  goToPrev(): void { if (this.pageSize !== -1) { this.goToPage(this.pageNo - 1); } }
  goToNext(): void { if (this.pageSize !== -1) { this.goToPage(this.pageNo + 1); } }
  goToLast(): void { if (this.pageSize !== -1) { this.goToPage(this.totalPages); } }

  // Print function (print only the report section, include section header only if not collapsed)
  printReport(): void {
    const reportContainer = document.querySelector('.report-container');
    if (!reportContainer) return;
    // Only print metadata if not collapsed
    if (this.sectionHeaderCollapsed) {
      // Temporarily hide metadata section for print
      const originalDisplay = (reportContainer.querySelector('.metadata-section') as HTMLElement)?.style.display;
      const metadataSection = reportContainer.querySelector('.metadata-section') as HTMLElement;
      if (metadataSection) metadataSection.style.display = 'none';
      setTimeout(() => {
        const printContents = reportContainer.innerHTML;
        const printWindow = window.open('', '', 'height=800,width=1200');
        if (printWindow) {
          printWindow.document.write('<html><head><title>' + (this.reportTitle || 'Report') + '</title>');
          // Add styles for print
          const styles = Array.from(document.querySelectorAll('style, link[rel=stylesheet]'));
          styles.forEach(style => {
            printWindow!.document.write(style.outerHTML);
          });
          printWindow.document.write('</head><body>');
          printWindow.document.write('<div class="report-container">' + printContents + '</div>');
          printWindow.document.write('</body></html>');
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
            // Restore metadata section display
            if (metadataSection) metadataSection.style.display = originalDisplay || '';
          }, 300);
        }
      }, 100);
    } else {
      // Print as usual
      const printContents = reportContainer.innerHTML;
      const printWindow = window.open('', '', 'height=800,width=1200');
      if (printWindow) {
        printWindow.document.write('<html><head><title>' + (this.reportTitle || 'Report') + '</title>');
        // Add styles for print
        const styles = Array.from(document.querySelectorAll('style, link[rel=stylesheet]'));
        styles.forEach(style => {
          printWindow!.document.write(style.outerHTML);
        });
        printWindow.document.write('</head><body>');
        printWindow.document.write('<div class="report-container">' + printContents + '</div>');
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 300);
      }
    }
  }

  // Download CSV function
  downloadCSV(): void {
    const csvRows = [];
    // Header
    csvRows.push(this.leafColumns.map(col => '"' + (col.title || col.key) + '"').join(','));
    // Data
    this.tableData.forEach(row => {
      const rowData = this.leafColumns.map(col => {
        let val = this.getValue(row, col.key);
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') val = val.replace(/"/g, '""');
        return '"' + val + '"';
      });
      csvRows.push(rowData.join(','));
    });
    // Download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${this.reportTitle || 'report'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  get totalPages(): number {
    if (this.pageSize === -1) return 1;
    return Math.ceil(this.tableData.length / this.pageSize) || 1;
  }

  // Toggle pin header/footer
  togglePinHeaderFooter(): void {
    this.pinHeaderFooter = !this.pinHeaderFooter;
  }
}